import differenceInDays from 'date-fns/differenceInDays';
import { useQuery } from '@apollo/react-hooks';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getApolloClient, isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';

import { EotBannerTrialDetailsQuery } from './graphql/EotBannerTrialDetailsQuery.graphql';
import type { EotBannerTrialDetailsQuery as EotBannerTrialDetailsQueryType } from './graphql/__types__/EotBannerTrialDetailsQuery';

interface TrialDetails {
	/** When true, then `billingSourceSystem` and `daysLeftInTrial` are ready for consumption */
	isTrialDataAvailable: boolean;
	/** CCP or HAMS */
	billingSourceSystem?: string;
	/**
	 * Days left until the end of the trial. Rounds up fractional days to one full day.
	 * When -1, either the data is not available or there's no trial, or the trial has ended.
	 */
	daysLeftInTrial: number;
}

const NO_RESULT = {
	isTrialDataAvailable: false,
	daysLeftInTrial: -1,
};

/**
 * Returns pertinent trial details from CC GraphQL
 *
 * @param skip Allows the hook to do no work
 */
export function useTrialDetails(skip: boolean): TrialDetails {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data, loading, error } = useQuery<EotBannerTrialDetailsQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		EotBannerTrialDetailsQuery,
		{
			client: getApolloClient(),
			skip,
		},
	);

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'failed',
				actionSubject: 'useTrialDetails',
				source: 'EotBanner',
				attributes: {
					growthFeature: 'EotBanner',
					errorMessage: error.message,
				},
			},
		}).fire();

		markErrorAsHandled(error);
	}

	if (
		skip ||
		loading ||
		error ||
		!data ||
		!data.license ||
		!data.license.trialEndDate ||
		!data.license.billingSourceSystem
	) {
		return NO_RESULT;
	}

	const trialEndDate = new Date(data.license.trialEndDate);
	const now = new Date();

	if (now >= trialEndDate) {
		return NO_RESULT;
	}

	const daysLeftInTrial = differenceInDays(trialEndDate, now) + 1;
	const billingSourceSystem = data.license.billingSourceSystem;

	return {
		isTrialDataAvailable: true,
		billingSourceSystem,
		daysLeftInTrial,
	};
}
